/**
 * L'installation est-elle terminée ?
 *
 * Deux choses en dépendent : la page ouverte par défaut, et la présence de
 * l'entrée « Installation » au menu. Un assistant de démarrage n'a rien à faire
 * en tête de menu une fois le démarrage passé — il devient un rappel permanent
 * de quelque chose qui est fait.
 *
 * Le résultat est mémorisé pour la durée de la session : cette information ne
 * change qu'en cochant une étape, et la relire à chaque rendu ferait une requête
 * de trop sur chaque navigation.
 */
import { useEffect, useState } from 'react';

import { api } from '../api/client';

export type EtatInstallation = 'inconnu' | 'en-cours' | 'terminee';

let memorise: EtatInstallation = 'inconnu';
let enVol: Promise<EtatInstallation> | null = null;

/** À appeler après avoir coché une étape, pour que le menu suive. */
export function oublierEtatInstallation(): void {
  memorise = 'inconnu';
  enVol = null;
}

function charger(): Promise<EtatInstallation> {
  enVol ??= api
    .onboarding()
    .then((o) => {
      memorise = o.status === 'COMPLETED' ? 'terminee' : 'en-cours';
      return memorise;
    })
    .catch(() => {
      // En cas d'échec on considère l'installation EN COURS : mieux vaut
      // afficher une entrée de menu superflue que masquer un écran nécessaire.
      memorise = 'en-cours';
      return memorise;
    })
    .finally(() => {
      enVol = null;
    });
  return enVol;
}

export function useInstallation(): EtatInstallation {
  const [etat, setEtat] = useState<EtatInstallation>(memorise);

  useEffect(() => {
    if (memorise !== 'inconnu') {
      setEtat(memorise);
      return;
    }
    let annule = false;
    void charger().then((e) => {
      if (!annule) setEtat(e);
    });
    return () => {
      annule = true;
    };
  }, []);

  return etat;
}
